import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Eye, Plus, Trash2, Webhook } from 'lucide-react';

import { PageHeader } from '../../../../components/ui/PageHeader';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';

import { deleteWebhook, fetchWebhooks } from '../../../../features/webhooks/webhooksThunks';
import { setWebhookFilters } from '../../../../features/webhooks/webhooksSlice';
import { selectFilteredWebhooks } from '../../../../features/webhooks/webhooksSelectors';
import { getStatusLabel, getSubstatusLabel } from '../../../../features/webhooks/webhooksUtils';

import AddWebhookModal from './components/AddWebhookModal';
import WebhookFilters from './components/WebhookFilters';

const WEBHOOKS_LIST_PATH = '/configuration/webhooks';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-CL') : '-');

const describe = (webhook) =>
  `${getStatusLabel(webhook.status)} / ${getSubstatusLabel(webhook.status, webhook.substatus)}`;

/** `/configuration/webhooks`: the seller's webhooks, one per status and substatus. */
const WebhookConfiguration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { webhooks, loading, error, filters } = useSelector((state) => state.webhooks);
  const filtered = useSelector(selectFilteredWebhooks);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchWebhooks());
  }, [dispatch]);

  // The legacy deleted on the first click, without asking.
  const handleDelete = async () => {
    setDeleting(true);
    await dispatch(deleteWebhook(toDelete.id));
    setDeleting(false);
    setToDelete(null);
  };

  const lastPage = Math.max(1, Math.ceil(filtered.length / filters.rowsPerPage));
  const page = Math.min(filters.page, lastPage);
  const rows = filtered.slice((page - 1) * filters.rowsPerPage, page * filters.rowsPerPage);

  const columns = [
    {
      header: 'ESTADO',
      cell: (row) => <span className="font-semibold">{getStatusLabel(row.status)}</span>,
    },
    { header: 'SUBESTADO', cell: (row) => getSubstatusLabel(row.status, row.substatus) },
    {
      header: 'URL DESTINO',
      cell: (row) => (
        <span className="block max-w-[320px] truncate text-text-secondary" title={row.url}>
          {row.url}
        </span>
      ),
    },
    { header: 'CREADO', cell: (row) => formatDate(row.created_at) },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Eye}
            aria-label={`Ver webhook de ${describe(row)}`}
            title="Ver detalle"
            onClick={() => navigate(`${WEBHOOKS_LIST_PATH}/${row.id}`)}
          />
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Trash2}
            aria-label={`Eliminar webhook de ${describe(row)}`}
            title="Eliminar"
            className="hover:text-danger"
            onClick={() => setToDelete(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <div data-testid="webhooks-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Webhook}
        iconClassName="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
        title="Webhooks"
        description="Avisa a tus sistemas cuando un envío cambia de estado."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setIsAddOpen(true)}
            data-testid="webhook-add"
          >
            Agregar webhook
          </Button>
        }
      />

      <WebhookFilters />

      <DataTable
        columns={columns}
        data={rows}
        totalItems={filtered.length}
        currentPage={page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchWebhooks())}
        onPageChange={(nextPage) => dispatch(setWebhookFilters({ page: nextPage }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setWebhookFilters({ rowsPerPage }))}
        itemLabel="webhooks"
        emptyMessage={
          webhooks.length === 0
            ? 'Aún no tienes webhooks. Usa "Agregar webhook" para avisar a tus sistemas.'
            : 'Ningún webhook coincide con los filtros.'
        }
      />

      <AddWebhookModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />

      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        onClose={() => !deleting && setToDelete(null)}
        onConfirm={handleDelete}
        title="Eliminar webhook"
        message={
          toDelete
            ? `Se elimina el webhook de ${describe(toDelete)}. Tu sistema dejará de recibir ese aviso.`
            : ''
        }
        loading={deleting}
        testId="webhook-delete-dialog"
      />
    </div>
  );
};

export default memo(WebhookConfiguration);
